import * as Api from '@ViewModels';
import * as React from 'react';
import { apiModelTypeToAutomationStepType } from '../extViewmodels/sdk/Utils';

// ============================================================================
// Type Guard Functions for Automation Steps
// ============================================================================

/**
 * Get the normalized step type enum from an automation step.
 * Use this for switch statements or when you need the enum value.
 */
export function getStepType(step: Api.IAutomationStep | undefined): Api.AutomationStepType | undefined {
	return step?._type ? apiModelTypeToAutomationStepType(step._type as Api.ApiModelType) : undefined;
}

/** Type guard: checks if step is an Email step */
export function isEmailStep(step: Api.IAutomationStep | undefined): step is Api.IEmailAutomationStep {
	return step?._type === 'EmailAutomationStep';
}

/** Type guard: checks if step is a Texting step */
export function isTextingStep(step: Api.IAutomationStep | undefined): step is Api.ITextingAutomationStep {
	return step?._type === 'TextingAutomationStep';
}

/** Type guard: checks if step is a Switch step */
export function isSwitchStep(step: Api.IAutomationStep | undefined): step is Api.ISwitchAutomationStep {
	return step?._type === 'SwitchAutomationStep';
}

/**
 * Broader predicate that matches either regular SwitchAutomationStep or BehaviorSwitchAutomationStep.
 * Both publish as child templates and inherit the root automation's anchoring rule, so callers that
 * care about "is this any kind of switch" (paste normalization, branch-terminus detection, etc.)
 * should use this instead of the narrower {@link isSwitchStep}.
 */
export function isAnySwitchStep(step: { _type?: unknown } | undefined): boolean {
	return step?._type === 'SwitchAutomationStep' || step?._type === 'BehaviorSwitchAutomationStep';
}

/** Type guard: checks if step is an AddTag step */
export function isAddTagStep(step: Api.IAutomationStep | undefined): step is Api.IAddTagAutomationStep {
	return step?._type === 'AddTagAutomationStep';
}

/** Type guard: checks if step is a RemoveTag step */
export function isRemoveTagStep(step: Api.IAutomationStep | undefined): step is Api.IRemoveTagAutomationStep {
	return step?._type === 'RemoveTagAutomationStep';
}

/** Type guard: checks if step is either AddTag or RemoveTag */
export function isTagStep(
	step: Api.IAutomationStep | undefined
): step is Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep {
	return isAddTagStep(step) || isRemoveTagStep(step);
}

/** Type guard: checks if step is an ActionItem step */
export function isActionItemStep(step: Api.IAutomationStep | undefined): step is Api.IActionItemAutomationStep {
	return step?._type === 'ActionItemAutomationStep';
}

/** Type guard: checks if step is a Handwritten Card step */
export function isHwcStep(step: Api.IAutomationStep | undefined): step is Api.HandwrittenCardAutomationStep {
	return step?._type === 'HandwrittenCardAutomationStep';
}

/** Type guard: checks if step is an HTML Newsletter step */
export function isHtmlNewsletterStep(step: Api.IAutomationStep | undefined): step is Api.IHtmlNewsletterAutomationStep {
	return step?._type === 'HtmlNewsletterAutomationStep';
}

/** Type guard: checks if step is a NoAction step */
export function isNoActionStep(step: Api.IAutomationStep | undefined): step is Api.INoActionAutomationStep {
	return step?._type === 'NoActionAutomationStep';
}

/** Type guard: checks if step is an Opportunity step */
export function isOpportunityStep(step: Api.IAutomationStep | undefined): step is Api.IOpportunityAutomationStep {
	return step?._type === 'OpportunityAutomationStep';
}

// ============================================================================
// Interfaces
// ============================================================================

export enum AutomationStepAction {
	Copy = 'copy',
	Delete = 'delete',
	MoveDown = 'moveDown',
	MoveUp = 'moveUp',
}

export interface IEditAutomationTemplateStepsContext {
	automationTemplateId?: string;
	impersonationContext?: Api.IImpersonationContext;
	unsavedStep?: Api.IAutomationStep;
	setUnsavedStep?: (step: Api.IAutomationStep | undefined) => void;
	/** Trigger from the parent template, used by child/segment templates that don't have their own trigger */
	parentTrigger?: Api.IAutomationTrigger;
	/** When true, allows editing Industry-scoped (sample) templates from the platform admin panel */
	isAdminContext?: boolean;
}

export const EditAutomationTemplateStepsContext = React.createContext<IEditAutomationTemplateStepsContext | undefined>(
	undefined
);

export function useEditAutomationTemplateStepsContext() {
	const context = React.useContext(EditAutomationTemplateStepsContext);
	if (context === undefined) {
		throw new Error(
			'useEditAutomationTemplateStepsContext must be used within EditAutomationTemplateStepsContext.Provider'
		);
	}
	return context;
}

export const EditAutomationTemplateItemIdPrefix = 'edit-automation-template-item-scroll-anchor';

export enum AutomationsReportingTableColumn {
	Communication = 'Communication',
	Policy = 'Policy',
	Recipient = 'Recipient',
	StatusAndActions = 'Status / Actions',
}
